import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send, Plus, Users as UsersIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const MessagingPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvData, setNewConvData] = useState({ member_id: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchConversations();
    fetchMembers();
  }, [token]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
    }
  }, [selectedConv]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API}/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/conversations`,
        { 
          member_id: newConvData.member_id,
          participant_ids: [user.id]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConversations([response.data, ...conversations]);
      setSelectedConv(response.data);
      setShowNewConv(false);
      setNewConvData({ member_id: '' });
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv) return;

    try {
      await axios.post(
        `${API}/conversations/${selectedConv.id}/messages`,
        { 
          conversation_id: selectedConv.id,
          content: newMessage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages(selectedConv.id);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading mb-6 md:mb-8">
          Messagerie
        </h1>

        {/* Mobile: toggle between conversations and messages */}
        <div className="lg:hidden mb-4">
          {selectedConv && (
            <button
              data-testid="back-to-conversations"
              onClick={() => setSelectedConv(null)}
              className="flex items-center gap-2 text-slate-600 hover:text-primary text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux conversations
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 h-auto lg:h-[calc(100vh-250px)]">
          {/* Conversations List - hidden on mobile when a conversation is selected */}
          <Card className={`lg:col-span-1 ${selectedConv ? 'hidden lg:block' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base md:text-lg">Conversations</CardTitle>
                <Button
                  size="sm"
                  onClick={() => setShowNewConv(!showNewConv)}
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {showNewConv && (
                <div className="p-4 border-b border-slate-100">
                  <form onSubmit={handleCreateConversation} className="space-y-3">
                    <select
                      value={newConvData.member_id}
                      onChange={(e) => setNewConvData({ member_id: e.target.value })}
                      className="w-full rounded-xl border-slate-200 bg-white px-4 py-2 text-sm"
                      required
                    >
                      <option value="">Sélectionner un bénéficiaire</option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.first_name} {member.last_name}
                        </option>
                      ))}
                    </select>
                    <Button type="submit" size="sm" className="w-full">
                      Créer
                    </Button>
                  </form>
                </div>
              )}
              <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                {conversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 text-sm">Aucune conversation</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConv(conv)}
                      className={`w-full p-4 text-left border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        selectedConv?.id === conv.id ? 'bg-primary-50' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-secondary-50 rounded-full">
                          <UsersIcon className="w-4 h-4 text-secondary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-primary-900 truncate">
                            {conv.member_name}
                          </p>
                          {conv.last_message && (
                            <p className="text-sm text-slate-600 truncate">
                              {conv.last_message}
                            </p>
                          )}
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Messages - hidden on mobile when no conversation is selected */}
          <Card className={`lg:col-span-2 flex flex-col ${!selectedConv ? 'hidden lg:flex' : ''}`}>
            {selectedConv ? (
              <>
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg">{selectedConv.member_name}</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 p-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-slate-600">Aucun message. Commencez la conversation !</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl p-4 ${
                              msg.sender_id === user.id
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 text-slate-900'
                            }`}
                          >
                            <p className="text-sm font-medium mb-1">{msg.sender_name}</p>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <div className="p-4 border-t border-slate-100">
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Input
                      data-testid="message-input"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Tapez votre message..."
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim()}
                      data-testid="send-message-btn"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Sélectionnez une conversation</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessagingPage;
